import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { ChatList } from '../chatlist/ChatList'
import { RueckfrageHost } from '../../components/Rueckfrage'
import { repariereAbo } from '../../lib/push'
import { merkeOrt } from '../../lib/letzterChat'
import { abonniereTippziel } from '../../lib/tippziel'
import { hoeheNachmessen } from '../../lib/tastatur'
import { useMe, useSettings, useTrip } from '../../hooks/queries'
import { blasenKette, blasenVariablen } from '../../lib/blasenfarben'
import {
  ausEinstellungen,
  ausKonto,
  listenKette,
  listenVariablen,
} from '../../lib/listenfarben'
import { vomKonto } from '../../lib/darstellung'
import { knopfVariablen } from '../../lib/knopffarbe'
import { useDunkel } from '../../hooks/useDunkel'
import { useUhrwechsel } from '../../hooks/useUhrwechsel'
import { NADELFARBEN } from '../map/nadelfarben'

/**
 * Zweispaltig am Rechner, einspaltig am Telefon.
 *
 * Welche Spalte auf dem Telefon zu sehen ist, entscheidet der Pfad: die
 * Startseite zeigt die Chatliste, jeder tiefere Pfad den geöffneten Inhalt.
 * Das ist bewusst nicht in einem Zustand gespeichert -- so bringt der
 * Zurück-Knopf des Browsers einen von selbst wieder zur Liste.
 */
export function Shell() {
  const location = useLocation()
  const navigate = useNavigate()
  const pane = location.pathname === '/' ? 'list' : 'thread'

  /**
   * Ein Tipp auf eine Benachrichtigung führt in ihren Chat.
   *
   * **Hier und nicht in `main.tsx`**, weil hier der Router steht: der Sprung
   * soll die Seite nicht neu laden. Zugehört wird schon vorher (siehe
   * `lib/tippziel.ts`) — was in der Zwischenzeit ankam, liefert das Abonnement
   * beim Anmelden nach.
   */
  useEffect(() => abonniereTippziel((pfad) => navigate(pfad)), [navigate])

  /**
   * Nach jedem Seitenwechsel die Höhe der Anwendung neu messen.
   *
   * **Genau der Moment aus dem Betrieb:** „Neustart der WebApp: alles ok; kein
   * Rand unten im Chat. Aufruf anderer Menüs (Einstellungen, Tastaturtest …)
   * — danach ist im Chat und in der Karte unten ein Rand."
   *
   * Wer auf einer anderen Seite in ein Feld getippt hat, hatte dort eine
   * Tastatur; in einer Web-App vom Home-Bildschirm verkleinert iOS dafür das
   * Fenster. `lib/tastatur.ts` misst deshalb gar nicht erst, solange ein Feld
   * den Fokus hat — und hier wird nachgeholt, was in der Zwischenzeit
   * ausgefallen ist. Ein Wechsel der Seite ist der Augenblick, in dem
   * niemand schreibt.
   */
  useEffect(() => {
    hoeheNachmessen(`Seite: ${location.pathname}`)
  }, [location.pathname])

  // Einmal je Start: hat dieses Gerät seine Anmeldung für Benachrichtigungen
  // noch? Sie kann ohne Zutun verschwinden, und dann kommt einfach nichts
  // mehr -- ohne dass irgendwo etwas danach aussieht. Stillschweigend
  // wiederherstellen ist hier richtig: gefragt wurde schon, die Erlaubnis
  // liegt vor. Geht es nicht, bleibt es dabei; der Hinweis steht dann unter
  // Einstellungen.
  useEffect(() => {
    void repariereAbo().catch(() => undefined)
  }, [])

  /**
   * Die Schriftgröße vom Konto — sobald sie da ist.
   *
   * **Hier und nicht auf der Einstellungsseite**, weil sie überall gilt und
   * nicht nur dort, wo man sie wählt. Beim Start steht schon die Kopie aus
   * dem Browser am Wurzelelement; das hier ist der Abgleich, wenn jemand sie
   * an einem anderen Gerät verstellt hat. `vomKonto` tut nichts, wenn beide
   * dasselbe sagen — sonst schriebe jeder Durchgang in den Speicher.
   */
  const schrift = useMe().data?.font_scale
  useEffect(() => {
    vomKonto(schrift)
  }, [schrift])

  const werte = useSettings().data?.settings

  /**
   * Welche Reise gerade offen ist — aus dem Pfad abgeleitet, nicht gemerkt.
   *
   * Jeder Weg in eine Reise beginnt mit `/t/{id}`; ein zweiter Zustand daneben
   * liefe irgendwann aus dem Tritt. `useTrip(0)` fragt nichts ab: die Abfrage
   * ist abgeschaltet, solange keine Reise offen ist.
   */
  const reiseId = Number(location.pathname.match(/^\/t\/(\d+)(\/|$)/)?.[1] ?? 0)
  const reise = useTrip(reiseId).data

  /**
   * Die Farben der Sprechblasen — am Wurzelelement, und **je Reise** (#49).
   *
   * **Hier und nicht in der Chatansicht**, weil dieselben Variablen auch den
   * Netzstreifen über der Eingabezeile und die Vorschau in den Einstellungen
   * tragen — und der Streifen liegt außerhalb des Kastens, in dem der Verlauf
   * steht. Ein blauer Streifen über roten Blasen wäre der Preis dafür, sie
   * weiter unten zu setzen. Und hier statt im Stylesheet, weil sie aus der
   * Datenbank kommen.
   *
   * **Ist eine Reise offen, gelten ihre Werte** — sonst die Voreinstellung aus
   * den Einstellungen. Kein Zusammenrechnen aus beidem: die Farbe der Reise
   * wurde beim Anlegen kopiert und steht seitdem für sich; `null` an der Reise
   * heißt „wie das Erscheinungsbild" und nicht „wie die Voreinstellung".
   *
   * `null` heißt: wieder wegnehmen. Wer die Farbe zurücksetzt, soll die des
   * Erscheinungsbilds zurückbekommen — auch die dunkle, und ohne Neuladen.
   */
  //
  // **Und in der Reise gewinnt die eigene Ansicht** (#52): was ein
  // Mitlesender für sich eingestellt hat, legt sich über die Farbe der Reise.
  // Die Kette steht als `blasenKette()` an einer Stelle.
  const inDerReise = reise === undefined
    ? null
    : blasenKette(reise.chat_bubbles_mine, reise.chat_bubbles)
  const eigene = inDerReise?.chat_own_color ?? (werte?.chat_own_color ?? '')
  const fremde = inDerReise?.chat_other_color ?? (werte?.chat_other_color ?? '')
  /**
   * **Solange die Reise noch unterwegs ist, wird nichts umgestellt.**
   *
   * Sonst trüge der Chat für einen Augenblick die Voreinstellung und danach
   * seine eigene Farbe — beim Wechsel von einem Bericht zum nächsten also ein
   * Aufblitzen in einem Ton, der zu keinem von beiden gehört. Der Prüflauf hat
   * genau das gemessen, bevor diese Zeile stand.
   */
  const wartetAufReise = reiseId > 0 && reise === undefined
  useEffect(() => {
    if (wartetAufReise) return
    const wurzel = document.documentElement
    const variablen = blasenVariablen({
      chat_own_color: eigene,
      chat_other_color: fremde,
    })
    for (const [name, wert] of Object.entries(variablen)) {
      if (wert === null) wurzel.style.removeProperty(name)
      else wurzel.style.setProperty(name, wert)
    }
  }, [eigene, fremde, wartetAufReise])

  /**
   * Und die Farbe der Knöpfe — dieselbe Stelle, dieselbe Regel.
   *
   * Sie hängt hier und nicht an einer Seite, weil sie überall gilt: der Knopf
   * „Freigeben" auf der Karte, „GPX importieren" auf der Infoseite, der
   * Sendeknopf im Chat. Was nicht gewählt ist, wird **weggenommen** statt
   * gesetzt — nur so gilt wieder, was das Erscheinungsbild vorgibt, und die
   * dunkle Darstellung behält ihren eigenen Ton.
   */
  const akzent = werte?.accent_color
  useEffect(() => {
    const wurzel = document.documentElement
    for (const [name, wert] of Object.entries(knopfVariablen(akzent ?? ''))) {
      if (wert === null) wurzel.style.removeProperty(name)
      else wurzel.style.setProperty(name, wert)
    }
  }, [akzent])

  /**
   * Und die Fläche der Kärtchen ohne Bild — ebenfalls am Wurzelelement.
   *
   * Eingestellt wird sie **auf der Karte**, weil man sie dort sieht. Gelten
   * muss sie aber auch für die Kärtchen im Chat, und die liegen außerhalb der
   * Karte. Deshalb hier noch einmal, aus dem gespeicherten Wert; auf der Karte
   * selbst gewinnt währenddessen der Regler, weil er sie am Kartenrahmen setzt.
   */
  const kaertchen = werte?.card_color
  useEffect(() => {
    const eintrag = NADELFARBEN.find((e) => e.key === 'card_color')!
    document.documentElement.style.setProperty(eintrag.variable, kaertchen ?? eintrag.vorgabe)
  }, [kaertchen])

  /**
   * Und die Farben der Reiseübersicht (#54) — dieselbe Stelle, dieselbe Regel.
   *
   * Zwei Stufen: was am **Konto** steht, schlägt die Voreinstellung; was leer
   * bleibt, wird **weggenommen** statt gesetzt, damit das Erscheinungsbild
   * gilt — auch das dunkle.
   *
   * Sie hängen hier und nicht an der Chatliste, weil die Liste am iPad in der
   * Seitenspalte steht und am Telefon die ganze Seite ist: zwei Orte, eine
   * Regel.
   */
  const konto = useMe().data
  const listen = listenKette(ausKonto(konto), ausEinstellungen(werte))
  /**
   * **Und im dunklen Modus wird umgerechnet statt weggenommen** (#73).
   *
   * Eingestellt wird eine Farbe im hellen Erscheinungsbild. Abends stand
   * dieselbe helle Fläche in einer schwarzen Liste und trug weiße Schrift —
   * gemessen 1,29:1. `listenVariablen()` dreht jetzt die Helligkeit und
   * rechnet die Schrift dazu; welche Fassung gerade gilt, weiß nur der
   * Browser, und `useDunkel()` hört ihm zu.
   */
  const dunkel = useDunkel()

  /**
   * **Und wandert die Uhr des Geräts, wird neu gezeichnet.**
   *
   * Hier, weil hier jeder Weg vorbeikommt: die Uhrzeiten stehen im Chat, in
   * der Reiseliste, am Highlight und im Buch. Der Rückgabewert wird nirgends
   * gelesen und das ist der Punkt — er ändert sich, und damit zeichnet der
   * ganze Baum darunter neu. `lib/format.ts` baut seine Formatierer dabei von
   * selbst neu; ohne das Neuzeichnen wird es aber nie danach gefragt.
   */
  useUhrwechsel()
  useEffect(() => {
    const wurzel = document.documentElement
    for (const [name, wert] of Object.entries(listenVariablen(listen, dunkel))) {
      if (wert === null) wurzel.style.removeProperty(name)
      else wurzel.style.setProperty(name, wert)
    }
  }, [listen.own, listen.other, listen.bg, dunkel])

  // Wo man war, wird hier gemerkt und nicht in den Seiten selbst: hier kommt
  // jeder Weg vorbei, und `merkeOrt` nimmt ohnehin nur an, was zählt.
  //
  // **Die Übersicht ganz zu Beginn zählt nicht mit.** Sie ist keine
  // Entscheidung, sondern die Tür: mit ihr macht die Anwendung auf, wenn nicht
  // gerade eine Adresse mitgegeben wurde. Sie mitzuschreiben hieße, die
  // Erinnerung mit sich selbst zu überschreiben.
  //
  // Das ist nicht theoretisch. Die `StandWache` lädt beim ersten Start nach
  // einem Upload einmal neu — und auf diesem verworfenen ersten Durchgang
  // stünde sonst schon „Übersicht" im Speicher. Genau so kam auf dem iPhone
  // die Liste, obwohl zuletzt ein Chat offen war. Ein Chat als erster Weg
  // wird dagegen mitgeschrieben: den hat jemand mitgebracht.
  const ersterWeg = useRef(true)
  useEffect(() => {
    const erster = ersterWeg.current
    ersterWeg.current = false
    if (erster && location.pathname === '/') return
    merkeOrt(location.pathname)
  }, [location.pathname])

  return (
    <div className="shell" data-pane={pane}>
      <aside className="sidebar">
        <ChatList />
      </aside>
      <main className="main">
        <Outlet />
      </main>
      <RueckfrageHost />
    </div>
  )
}

